import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Modal, Spinner, Table } from 'react-bootstrap';
import medicalPrescriptionService from '../../services/medicalPrescriptionService';
import Icon from '../common/Icons';
import { formatAppDateTime } from '../../utils/appTimezone';
import { computeFloatingMenuPosition, estimateActionMenuHeight } from '../../utils/floatingMenuPosition';
import ConsultationPrescriptionPanel from './ConsultationPrescriptionPanel';
import MedicalPrescriptionForm from './MedicalPrescriptionForm';
import {
  canEditPrescription,
  pharmacyStatusLabel,
  prescriptionToConsultationStub
} from './utils/prescriptionPharmacyEditability';
import './ConsultationEditorPage.css';
import './ConsultationStatusBadge.css';
import './MedicalPrescriptionList.css';
import '../head-doctor/HeadDoctorDashboard.css';

const STATUS_FILTERS = [
  { value: 'ALL', label: 'Tous' },
  { value: 'ACTIVE', label: 'Actives', tone: 'queue' },
  { value: 'FULFILLED', label: 'Remplies', tone: 'completed' },
  { value: 'CANCELLED', label: 'Annulées', tone: 'cancelled' }
];

function statusPill(status) {
  switch (String(status || '').toUpperCase()) {
    case 'ACTIVE':
      return <span className="hd-rx-status hd-rx-status--active">Active</span>;
    case 'FULFILLED':
      return <span className="hd-rx-status hd-rx-status--fulfilled">Remplie</span>;
    case 'CANCELLED':
      return <span className="hd-rx-status hd-rx-status--cancelled">Annulée</span>;
    default:
      return <span className="hd-rx-status">{status || '—'}</span>;
  }
}

/**
 * Liste des ordonnances — même panneau rédactionnel que ConsultationEditorPage.
 * Édition ouverte tant que la pharmacie n’a pas délivré.
 */
const MedicalPrescriptionList = ({
  showHeader = true,
  headDoctorMode = false,
  workSiteId = '',
  workSiteName = '',
  onMutated
}) => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editorRx, setEditorRx] = useState(null);
  const [actionMenuRx, setActionMenuRx] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0, placement: 'below' });
  const [filters, setFilters] = useState({
    patient_name: '',
    status: 'ALL',
    date_from: '',
    date_to: ''
  });

  const getPrimaryActionLabel = (prescription) =>
    canEditPrescription(prescription) ? 'Modifier l’ordonnance' : 'Voir l’ordonnance';

  const openActionMenu = (event, prescription) => {
    const editable = canEditPrescription(prescription);
    const canDelete = prescription.status === 'ACTIVE';
    const itemCount = 1 + (editable ? 1 : 0) + (canDelete ? 1 : 0);
    const rect = event.currentTarget.getBoundingClientRect();
    setActionMenuPos(
      computeFloatingMenuPosition(rect, {
        menuWidth: 240,
        menuHeight: estimateActionMenuHeight(itemCount, { withSeparator: canDelete })
      })
    );
    setActionMenuRx(prescription);
  };

  const closeActionMenu = () => setActionMenuRx(null);

  const loadPrescriptions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const filterParams = {};
      if (filters.patient_name) filterParams.patient_name = filters.patient_name;
      if (filters.status !== 'ALL') filterParams.status = filters.status;
      if (filters.date_from) filterParams.date_from = filters.date_from;
      if (filters.date_to) filterParams.date_to = filters.date_to;
      if (workSiteId) filterParams.site_id = workSiteId;

      const response = await medicalPrescriptionService.getPrescriptions(filterParams);
      if (response.success) {
        setPrescriptions(response.data || []);
      }
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des ordonnances');
    } finally {
      setLoading(false);
    }
  }, [filters, workSiteId]);

  useEffect(() => {
    loadPrescriptions();
  }, [loadPrescriptions]);

  const stats = useMemo(() => {
    const total = prescriptions.length;
    const active = prescriptions.filter((p) => p.status === 'ACTIVE').length;
    const fulfilled = prescriptions.filter((p) => p.status === 'FULFILLED').length;
    const cancelled = prescriptions.filter((p) => p.status === 'CANCELLED').length;
    const editable = prescriptions.filter((p) => canEditPrescription(p)).length;
    return { total, active, fulfilled, cancelled, editable };
  }, [prescriptions]);

  const hasActiveFilters =
    Boolean(filters.patient_name) ||
    filters.status !== 'ALL' ||
    Boolean(filters.date_from) ||
    Boolean(filters.date_to);

  const resetFilters = () =>
    setFilters({ patient_name: '', status: 'ALL', date_from: '', date_to: '' });

  const handleCancel = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette ordonnance ?')) return;
    try {
      await medicalPrescriptionService.cancelPrescription(id);
      loadPrescriptions();
      onMutated?.();
    } catch (err) {
      alert(err.message || "Erreur lors de l'annulation");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette ordonnance ?')) return;
    try {
      await medicalPrescriptionService.deletePrescription(id);
      loadPrescriptions();
      onMutated?.();
    } catch (err) {
      alert(err.message || 'Erreur lors de la suppression');
    }
  };

  const openEditor = (prescription) => {
    setEditorRx(prescription);
  };

  const closeEditor = () => setEditorRx(null);

  const handleEditorSuccess = () => {
    loadPrescriptions();
    onMutated?.();
  };

  const handleCreateSuccess = () => {
    loadPrescriptions();
    setShowCreateForm(false);
    onMutated?.();
  };

  const editorStub = editorRx ? prescriptionToConsultationStub(editorRx) : null;
  const editorCanEdit = editorRx ? canEditPrescription(editorRx) : false;

  return (
    <div className={`hd-co-shell hd-rx-list${headDoctorMode ? ' hd-rx-list--hd' : ''}`}>
      {showHeader ? (
        <div className="hd-rx-list__legacy-head d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Ordonnances médicales</h4>
            <p className="text-muted mb-0">Même rédaction que la fiche clinique · édition avant délivrance</p>
          </div>
          <button type="button" className="hd-co-row-action" onClick={() => setShowCreateForm(true)}>
            + Nouvelle ordonnance
          </button>
        </div>
      ) : null}

      <section className="hd-co-stats" aria-label="Synthèse des ordonnances">
        <article className="hd-co-stat-card">
          <span className="hd-co-stat-card__label">Total</span>
          <span className="hd-co-stat-card__value">{loading ? '—' : stats.total}</span>
          <span className="hd-co-stat-card__hint">{workSiteName || 'Site actif'}</span>
        </article>
        <button
          type="button"
          className={`hd-co-stat-card hd-co-stat-card--queue${filters.status === 'ACTIVE' ? ' is-active' : ''}`}
          onClick={() => setFilters((f) => ({ ...f, status: 'ACTIVE' }))}
        >
          <span className="hd-co-stat-card__label">Actives</span>
          <span className="hd-co-stat-card__value">{loading ? '—' : stats.active}</span>
          <span className="hd-co-stat-card__hint">{stats.editable} modifiables</span>
        </button>
        <button
          type="button"
          className={`hd-co-stat-card hd-co-stat-card--success${filters.status === 'FULFILLED' ? ' is-active' : ''}`}
          onClick={() => setFilters((f) => ({ ...f, status: 'FULFILLED' }))}
        >
          <span className="hd-co-stat-card__label">Remplies</span>
          <span className="hd-co-stat-card__value">{loading ? '—' : stats.fulfilled}</span>
        </button>
        <button
          type="button"
          className={`hd-co-stat-card hd-co-stat-card--inprogress${filters.status === 'CANCELLED' ? ' is-active' : ''}`}
          onClick={() => setFilters((f) => ({ ...f, status: 'CANCELLED' }))}
        >
          <span className="hd-co-stat-card__label">Annulées</span>
          <span className="hd-co-stat-card__value">{loading ? '—' : stats.cancelled}</span>
        </button>
      </section>

      <div className="hd-co-toolbar">
        <div className="hd-co-search-wrap">
          <Icon name="search" size={16} className="hd-co-search-icon" aria-hidden />
          <input
            type="search"
            className="hd-co-search-input"
            placeholder="Rechercher un patient…"
            value={filters.patient_name}
            onChange={(e) => setFilters({ ...filters, patient_name: e.target.value })}
            aria-label="Rechercher un patient"
          />
        </div>
        <div className="hd-co-toolbar-end">
          <div className="co-status-filter-bar" role="group" aria-label="Filtrer par statut">
            {STATUS_FILTERS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={[
                  'co-status-filter-btn',
                  opt.tone ? `co-status-filter-btn--${opt.tone}` : '',
                  filters.status === opt.value ? 'is-active' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setFilters({ ...filters, status: opt.value })}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="hd-co-refresh-btn"
            onClick={loadPrescriptions}
            disabled={loading}
            aria-label="Actualiser"
            title="Actualiser"
          >
            <Icon name="refresh" size={18} aria-hidden />
          </button>
          {!showHeader ? (
            <button type="button" className="hd-co-row-action" onClick={() => setShowCreateForm(true)}>
              + Nouvelle
            </button>
          ) : null}
          <div className="hd-co-toolbar-meta">
            <strong>{loading ? '—' : prescriptions.length}</strong>
            <span>résultats</span>
          </div>
        </div>
      </div>

      <div className="hd-co-toolbar-dates">
        <span className="hd-co-toolbar-dates__label">Période</span>
        <label className="hd-co-date-field">
          <span>Du</span>
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
          />
        </label>
        <label className="hd-co-date-field">
          <span>Au</span>
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
          />
        </label>
        {hasActiveFilters ? (
          <button type="button" className="hd-co-reset-btn" onClick={resetFilters}>
            Réinitialiser
          </button>
        ) : null}
      </div>

      {error ? <Alert variant="danger" className="hd-co-alert mb-0">{error}</Alert> : null}

      {loading ? (
        <div className="hd-co-state" role="status" aria-live="polite">
          <Spinner animation="border" className="hd-co-state__spinner" />
          <p className="hd-co-state__text mb-0">Chargement des ordonnances…</p>
        </div>
      ) : prescriptions.length === 0 ? (
        <div className="hd-co-state hd-co-state--empty">
          <div className="hd-co-state__icon" aria-hidden>
            <Icon name="pills" size={32} />
          </div>
          <h3 className="hd-co-state__title">Aucune ordonnance</h3>
          <p className="hd-co-state__text mb-0">
            Les ordonnances rédigées depuis une consultation apparaissent ici.
          </p>
        </div>
      ) : (
        <div className="hd-co-table-wrap table-responsive">
          <Table hover className="align-middle hd-consult-list__table mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Patient</th>
                <th>Médicaments</th>
                <th>Pharmacie</th>
                <th>Statut</th>
                <th className="text-end hd-consult-list__col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {prescriptions.map((prescription) => {
                const pharmacyLabel = pharmacyStatusLabel(prescription.pharmacy_status);
                const linesPreview = (prescription.items || [])
                  .slice(0, 2)
                  .map((item) => item.medication_name)
                  .filter(Boolean)
                  .join(', ');
                return (
                  <tr key={prescription.id}>
                    <td className="hd-consult-date">
                      {formatAppDateTime(prescription.prescribed_date) || '—'}
                    </td>
                    <td>
                      <div className="hd-consult-patient">
                        <strong className="hd-consult-patient__name">{prescription.patient_name}</strong>
                        {prescription.patient_phone ? (
                          <div className="hd-consult-patient__meta">{prescription.patient_phone}</div>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <div className="hd-rx-meds">
                        <span className="hd-rx-meds__count">
                          {(prescription.items || []).length} ligne
                          {(prescription.items || []).length > 1 ? 's' : ''}
                        </span>
                        {linesPreview ? (
                          <span className="hd-rx-meds__preview" title={linesPreview}>
                            {linesPreview}
                            {(prescription.items || []).length > 2 ? '…' : ''}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {pharmacyLabel ? (
                        <span
                          className={`hd-rx-pharmacy hd-rx-pharmacy--${String(
                            prescription.pharmacy_status || ''
                          ).toLowerCase()}`}
                        >
                          {prescription.pharmacy_dispensation_number
                            ? `${prescription.pharmacy_dispensation_number} · `
                            : ''}
                          {pharmacyLabel}
                        </span>
                      ) : (
                        <span className="text-muted">Non transmise</span>
                      )}
                    </td>
                    <td>{statusPill(prescription.status)}</td>
                    <td className="text-end">
                      <div className="d-inline-flex align-items-center justify-content-end gap-2">
                        <button
                          type="button"
                          className="hd-co-row-action"
                          onClick={() => openEditor(prescription)}
                        >
                          {getPrimaryActionLabel(prescription)}
                        </button>
                        <button
                          type="button"
                          className="hd-exam-actions-menu__toggle"
                          onClick={(e) => openActionMenu(e, prescription)}
                          aria-label={`Actions pour ${prescription.patient_name || 'ordonnance'}`}
                          title="Actions"
                        >
                          <Icon name="ellipsis-vertical" size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {actionMenuRx ? (
        <>
          <button
            type="button"
            className="hd-exam-actions-menu__backdrop"
            aria-label="Fermer le menu actions"
            onClick={closeActionMenu}
          />
          <div
            className={`hd-exam-actions-menu__floating${
              actionMenuPos.placement === 'above' ? ' hd-exam-actions-menu__floating--above' : ''
            }`}
            style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
            role="menu"
          >
            <button
              type="button"
              className="hd-exam-actions-menu__item"
              role="menuitem"
              onClick={() => {
                closeActionMenu();
                openEditor(actionMenuRx);
              }}
            >
              <Icon name={canEditPrescription(actionMenuRx) ? 'edit' : 'eye'} size={15} />
              <span>{getPrimaryActionLabel(actionMenuRx)}</span>
            </button>
            {canEditPrescription(actionMenuRx) ? (
              <button
                type="button"
                className="hd-exam-actions-menu__item"
                role="menuitem"
                onClick={() => {
                  closeActionMenu();
                  handleCancel(actionMenuRx.id);
                }}
              >
                <Icon name="eyeSlash" size={15} />
                <span>Annuler l’ordonnance</span>
              </button>
            ) : null}
            {actionMenuRx.status === 'ACTIVE' ? (
              <>
                <div className="hd-exam-actions-menu__separator" />
                <button
                  type="button"
                  className="hd-exam-actions-menu__item hd-exam-actions-menu__danger"
                  role="menuitem"
                  onClick={() => {
                    closeActionMenu();
                    handleDelete(actionMenuRx.id);
                  }}
                >
                  <Icon name="trash" size={15} />
                  <span>Supprimer</span>
                </button>
              </>
            ) : null}
          </div>
        </>
      ) : null}

      <MedicalPrescriptionForm
        show={showCreateForm}
        onHide={() => setShowCreateForm(false)}
        onSuccess={handleCreateSuccess}
      />

      <Modal
        show={Boolean(editorRx)}
        onHide={closeEditor}
        size="lg"
        centered
        scrollable
        dialogClassName="hd-rx-editor-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title as="div" className="w-100">
            <div className="consult-side-panel__head px-0 pt-1">
              <span className="consult-side-panel__icon" aria-hidden>
                💊
              </span>
              <div>
                <h2 className="consult-side-panel__title mb-0">
                  {editorCanEdit ? 'Modifier l’ordonnance' : 'Ordonnance médicale'}
                </h2>
                <p className="consult-side-panel__hint mb-0">
                  {editorRx?.patient_name || 'Patient'}
                  {editorCanEdit
                    ? ' · modifiable tant que non délivrée en pharmacie'
                    : ' · consultation seule (déjà traitée ou clôturée)'}
                </p>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {editorRx && editorStub ? (
            <section className="consult-side-panel consult-side-panel--rx hd-rx-editor-panel">
              <div className="consult-side-panel__body">
                <ConsultationPrescriptionPanel
                  key={editorRx.id}
                  consultation={editorStub}
                  selectedSiteId={workSiteId || editorRx.site_id || null}
                  selectedSiteName={workSiteName || editorRx.site_name || ''}
                  focusPrescriptionId={editorRx.id}
                  readOnly={!editorCanEdit}
                  onPrescriptionSuccess={handleEditorSuccess}
                />
              </div>
            </section>
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MedicalPrescriptionList;
